/**
 * Staging + upload state for ticket-chat attachments.
 *
 * Files upload eagerly the moment they're picked, so tapping Send is instant
 * even for a 10 MB photo — by then the bytes are already in S3 and all that's
 * left is the message POST. Each staged item carries its own status so one
 * failed upload doesn't block sending the rest.
 */
import { useCallback, useRef, useState } from "react";

import { requestTicketAttachmentUploadUrl, type Attachment } from "@/lib/ticket-api";
import { getLocalFileSize, uploadFromUri } from "@/lib/upload-from-uri";
import {
    pickFromCamera,
    pickFromPhotoLibrary,
    pickPdfDocument,
    type PickedUpload,
    type PickResult
} from "@/utils/pick-upload";

/** Mirrors MAX_ATTACHMENT_BYTES on the server. Checked here too so the nurse
 *  gets an instant, specific error instead of a rejected presign. */
const MAX_ATTACHMENT_BYTES = 15 * 1024 * 1024;

/** Mirrors the server's per-message cap and the photo picker's selectionLimit. */
export const MAX_ATTACHMENTS_PER_MESSAGE = 10;

export interface StagedAttachment {
    /** Local-only id for list keys and removal. */
    localId: string;
    uri: string;
    filename: string;
    mimeType: string;
    size: number;
    status: "uploading" | "ready" | "failed";
    /** Canonical s3Url, set once the PUT succeeds. */
    s3Url?: string;
    error?: string;
}

export type AttachmentSource = "camera" | "library" | "document";

function makeLocalId(): string {
    return `att-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function useChatAttachments(ticketId: number) {
    const [staged, setStaged] = useState<StagedAttachment[]>([]);
    const [pickerError, setPickerError] = useState<string | null>(null);
    // Guards against a double-tap opening two pickers.
    const pickingRef = useRef(false);

    const patch = useCallback((localId: string, next: Partial<StagedAttachment>) => {
        setStaged((prev) => prev.map((a) => (a.localId === localId ? { ...a, ...next } : a)));
    }, []);

    /** Presign → PUT to S3 → mark ready. Errors are captured per-item. */
    const uploadOne = useCallback(
        async (item: StagedAttachment) => {
            try {
                const { data } = await requestTicketAttachmentUploadUrl(
                    ticketId,
                    item.filename,
                    item.mimeType,
                    item.size
                );
                await uploadFromUri({
                    uri: item.uri,
                    uploadUrl: data.uploadUrl,
                    contentType: item.mimeType
                });
                patch(item.localId, { status: "ready", s3Url: data.s3Url });
            } catch (e) {
                patch(item.localId, {
                    status: "failed",
                    error: e instanceof Error ? e.message : "Upload failed"
                });
            }
        },
        [ticketId, patch]
    );

    const stageAndUpload = useCallback(
        async (picked: PickedUpload[]) => {
            const room = MAX_ATTACHMENTS_PER_MESSAGE - staged.length;
            if (room <= 0) {
                setPickerError(`You can attach up to ${MAX_ATTACHMENTS_PER_MESSAGE} files.`);
                return;
            }
            if (picked.length > room) {
                setPickerError(`Only the first ${room} file(s) were added.`);
            }

            const accepted: StagedAttachment[] = [];
            for (const p of picked.slice(0, room)) {
                const size = await getLocalFileSize(p.uri);
                if (size > MAX_ATTACHMENT_BYTES) {
                    setPickerError(`"${p.filename}" is larger than 15 MB.`);
                    continue;
                }
                // The presign pins ContentLength, so a file we can't measure
                // can't be uploaded. Surface it rather than sending size 0 and
                // taking an opaque 400.
                if (size === 0) {
                    setPickerError(`Couldn't read "${p.filename}". Try picking it again.`);
                    continue;
                }
                accepted.push({
                    localId: makeLocalId(),
                    uri: p.uri,
                    filename: p.filename,
                    mimeType: p.contentType,
                    size,
                    status: "uploading"
                });
            }
            if (accepted.length === 0) {
                return;
            }

            setStaged((prev) => [...prev, ...accepted]);
            // Fire uploads in parallel — each patches its own row on completion.
            accepted.forEach((item) => {
                void uploadOne(item);
            });
        },
        [staged.length, uploadOne]
    );

    const pick = useCallback(
        async (source: AttachmentSource) => {
            if (pickingRef.current) {
                return;
            }
            pickingRef.current = true;
            setPickerError(null);
            try {
                let result: PickResult;
                if (source === "camera") {
                    result = await pickFromCamera();
                } else if (source === "library") {
                    result = await pickFromPhotoLibrary();
                } else {
                    result = await pickPdfDocument();
                }

                if (result.status === "denied") {
                    setPickerError(
                        source === "camera"
                            ? "Camera access is off. Enable it in Settings to take a photo."
                            : "Photo access is off. Enable it in Settings to attach photos."
                    );
                    return;
                }
                if (result.status === "cancelled") {
                    return;
                }
                await stageAndUpload(result.value);
            } finally {
                pickingRef.current = false;
            }
        },
        [stageAndUpload]
    );

    const remove = useCallback((localId: string) => {
        setStaged((prev) => prev.filter((a) => a.localId !== localId));
    }, []);

    const retry = useCallback(
        (localId: string) => {
            setStaged((prev) => {
                const item = prev.find((a) => a.localId === localId);
                if (item) {
                    void uploadOne({ ...item, status: "uploading" });
                }
                return prev.map((a) =>
                    a.localId === localId
                        ? { ...a, status: "uploading" as const, error: undefined }
                        : a
                );
            });
        },
        [uploadOne]
    );

    const clear = useCallback(() => {
        setStaged([]);
        setPickerError(null);
    }, []);

    /** The uploaded subset, in the wire shape the send endpoint expects. */
    const toAttachments = useCallback((): Attachment[] => {
        return staged.flatMap((a) =>
            a.status === "ready" && a.s3Url
                ? [
                      {
                          type: a.mimeType.startsWith("image/") ? "image" : "document",
                          url: a.s3Url,
                          filename: a.filename,
                          size: a.size,
                          mimeType: a.mimeType
                      }
                  ]
                : []
        );
    }, [staged]);

    return {
        staged,
        pickerError,
        setPickerError,
        pick,
        remove,
        retry,
        clear,
        toAttachments,
        hasPending: staged.some((a) => a.status === "uploading"),
        hasFailed: staged.some((a) => a.status === "failed"),
        readyCount: staged.filter((a) => a.status === "ready").length
    };
}
